import { Injectable } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  PistaEstadoFetchResult,
  PistaLiveSnapshot,
  PistaLiveWatchOptions
} from '../models/moderacion/pista-live.dto';
import { PistaLiveDebugService } from '../services/pista-live-debug.service';
import { PistaLiveDataService } from './pista-live-data.service';
import { PistaLiveTransport } from './pista-live-transport';

@Injectable({
  providedIn: 'root'
})
export class PistaLiveSignalrTransportService implements PistaLiveTransport {
  private activeWatchers = 0;
  private activeExposicionId: number | null = null;
  private readonly defaultFallbackIntervalMs = this.resolvePositiveInt(
    environment.FEATURES?.['pistaLivePollingIntervalMs'],
    10000
  );

  constructor(
    private readonly pistaLiveDataService: PistaLiveDataService,
    private readonly pistaLiveDebugService: PistaLiveDebugService
  ) {}

  public watch(exposicionId: number, options: PistaLiveWatchOptions = {}): Observable<PistaLiveSnapshot> {
    const normalizedExposicionId = this.toPositiveInt(exposicionId);
    const fallbackIntervalMs = this.resolvePositiveInt(options.intervalMs, this.defaultFallbackIntervalMs);
    if (!normalizedExposicionId) {
      return of(this.buildInvalidSnapshot(exposicionId));
    }

    return new Observable<PistaLiveSnapshot>((subscriber) => {
      this.activeWatchers += 1;
      let disposed = false;
      let changedSub: Subscription | null = null;
      let fetchSub: Subscription | null = null;
      let fallbackTimerId: ReturnType<typeof setInterval> | null = null;
      let previousSignature = '';
      let fetchInFlight = false;
      let fetchQueued = false;

      const scheduleFetch = (): void => {
        if (disposed) {
          return;
        }

        if (fetchInFlight) {
          fetchQueued = true;
          return;
        }

        fetchInFlight = true;
        fetchQueued = false;
        fetchSub?.unsubscribe();
        fetchSub = this.pistaLiveDataService.listByExposicion(normalizedExposicionId).subscribe({
          next: (result) => {
            const snapshot = this.mapResultToSnapshot(result);
            if (snapshot.hash !== previousSignature) {
              previousSignature = snapshot.hash;
              subscriber.next(snapshot);
            }
          },
          error: () => {
            // `PistaLiveDataService` ya normaliza errores a resultado; no esperamos llegar aca.
          },
          complete: () => {
            fetchInFlight = false;
            if (fetchQueued) {
              scheduleFetch();
            }
          }
        });
      };

      this.activateExpo(normalizedExposicionId);
      scheduleFetch();
      fallbackTimerId = setInterval(() => {
        if (disposed) {
          return;
        }

        if (this.pistaLiveDebugService.isConnectedToExpo(normalizedExposicionId)) {
          return;
        }

        scheduleFetch();
      }, fallbackIntervalMs);

      changedSub = this.pistaLiveDebugService.changed$.subscribe((event) => {
        if (event.exposicionId !== normalizedExposicionId) {
          return;
        }

        scheduleFetch();
      });

      return () => {
        disposed = true;
        if (fallbackTimerId !== null) {
          clearInterval(fallbackTimerId);
          fallbackTimerId = null;
        }
        changedSub?.unsubscribe();
        fetchSub?.unsubscribe();
        this.activeWatchers = Math.max(0, this.activeWatchers - 1);
        if (this.activeWatchers === 0) {
          this.activeExposicionId = null;
          void this.pistaLiveDebugService.stop();
        }
      };
    });
  }

  private activateExpo(exposicionId: number): void {
    if (this.activeExposicionId === null) {
      this.activeExposicionId = exposicionId;
      void this.pistaLiveDebugService.start(exposicionId);
      return;
    }

    if (this.activeExposicionId === exposicionId) {
      void this.pistaLiveDebugService.start(exposicionId);
      return;
    }

    this.activeExposicionId = exposicionId;
    void this.pistaLiveDebugService.switchExpo(exposicionId);
  }

  private mapResultToSnapshot(result: PistaEstadoFetchResult): PistaLiveSnapshot {
    return {
      exposicionId: result.exposicionId,
      estados: result.estados,
      enCursoScopeKeys: result.estados
        .filter((item) => item.scope !== 'producto')
        .map((item) => item.identity),
      enPistaProductoIds: Array.from(
        new Set(
          result.estados
            .filter((item) => item.scope === 'producto' && !!item.productoId)
            .map((item) => item.productoId as number)
        )
      ),
      hash: result.hash,
      hasError: result.hasError,
      forbidden: result.forbidden,
      message: result.message,
      source: result.source,
      polledAtUtc: new Date().toISOString()
    };
  }

  private buildInvalidSnapshot(exposicionId: number): PistaLiveSnapshot {
    return {
      exposicionId,
      estados: [],
      enCursoScopeKeys: [],
      enPistaProductoIds: [],
      hash: 'invalid-exposicion',
      hasError: true,
      forbidden: false,
      message: 'Exposicion invalida.',
      source: 'public',
      polledAtUtc: new Date().toISOString()
    };
  }

  private toPositiveInt(value: unknown): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return 0;
    }

    return Math.trunc(numeric);
  }

  private resolvePositiveInt(value: unknown, fallback: number): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      return fallback;
    }

    return Math.trunc(numeric);
  }
}
